import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { ImageUpload } from '../components/ImageUpload';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Layers, Loader2, AlertTriangle, Plus, Search, Star, Archive, RotateCcw, Trash2, Pencil,
  Link2, ExternalLink, History, ShieldAlert, X, CheckCircle2, XCircle, Eye, RefreshCw, Tag,
} from 'lucide-react';
import { fmtDateTime } from '../lib/dateFormat';

const STATUS_CLS = {
  draft: 'bg-muted text-muted-foreground', published: 'bg-emerald-500/15 text-emerald-700',
  hidden: 'bg-amber-500/15 text-amber-700', archived: 'bg-muted text-muted-foreground',
  out_of_stock: 'bg-red-500/15 text-red-700', pending_provider: 'bg-blue-500/15 text-blue-700',
  scheduled: 'bg-blue-500/15 text-blue-700', expired: 'bg-red-500/15 text-red-700',
  active: 'bg-emerald-500/15 text-emerald-700', disabled: 'bg-amber-500/15 text-amber-700',
};

const RESEED = {
  store_product: api.adminStoreReseed, tour: api.adminToursReseed, esim_plan: api.adminEsimReseed,
};

const publicUrl = (key, it) => {
  const slug = it.slug || it.id;
  const map = {
    store_product: `/store/${slug}`, tour: `/tours/${slug}`, experience: '/experiences',
    esim_plan: '/esim', destination: `/destinations/${slug}`, deal: '/deals',
  };
  return map[key] || null;
};

// ------------------------------- Field renderer -------------------------------
const FieldInput = ({ field, value, onChange }) => {
  const { name, type, options, placeholder, help } = field;
  const set = (v) => onChange(name, v);
  if (type === 'textarea') return <Textarea rows={3} value={value || ''} onChange={(e) => set(e.target.value)} placeholder={placeholder} data-testid={`field-${name}`} />;
  if (type === 'switch') return <label className="flex items-center gap-2 text-sm pt-1"><Switch checked={!!value} onCheckedChange={set} data-testid={`field-${name}`} /><span className="text-muted-foreground">{value ? 'Yes' : 'No'}</span></label>;
  if (type === 'number') return <Input type="number" value={value ?? ''} onChange={(e) => set(e.target.value === '' ? '' : Number(e.target.value))} placeholder={placeholder} data-testid={`field-${name}`} />;
  if (type === 'select' && options?.length) return (
    <Select value={value || ''} onValueChange={set}><SelectTrigger data-testid={`field-${name}`}><SelectValue placeholder="Select…" /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
  );
  if (type === 'image') return <ImageUpload variant="cover" value={value} onChange={set} testId={`field-${name}`} />;
  if (type === 'multiimage') {
    const arr = Array.isArray(value) ? value : (value ? [value] : []);
    return (
      <div className="space-y-2">
        {arr.length > 0 && <div className="flex flex-wrap gap-2">{arr.map((u, i) => (
          <div key={i} className="relative h-16 w-16 rounded-lg overflow-hidden border border-border group">
            <img src={api.mediaUrl(u)} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => set(arr.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/60 text-white p-0.5 rounded-bl-lg" data-testid={`field-${name}-remove-${i}`}><X className="h-3 w-3" /></button>
          </div>
        ))}</div>}
        <ImageUpload variant="cover" value="" onChange={(u) => u && set([...arr, u])} testId={`field-${name}-add`} />
      </div>
    );
  }
  if (type === 'tags') {
    const arr = Array.isArray(value) ? value : (value ? String(value).split(',').map((s) => s.trim()) : []);
    return <Input value={arr.join(', ')} onChange={(e) => set(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} placeholder={placeholder || 'comma, separated, values'} data-testid={`field-${name}`} />;
  }
  return <Input value={value ?? ''} onChange={(e) => set(e.target.value)} placeholder={placeholder} data-testid={`field-${name}`} />;
};

// ------------------------------- Add / Edit form -------------------------------
const EntityForm = ({ entity, initial, onClose, onSaved }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => {
    const base = {};
    (entity.fields || []).forEach((f) => { base[f.name] = initial ? initial[f.name] : (f.type === 'switch' ? false : (f.type === 'tags' || f.type === 'multiimage' ? [] : '')); });
    base.__status = initial ? (initial.status || 'draft') : 'draft';
    return base;
  });
  const [saving, setSaving] = useState(false);
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const groups = useMemo(() => {
    const g = {};
    (entity.fields || []).forEach((f) => { (g[f.group] = g[f.group] || []).push(f); });
    return g;
  }, [entity]);

  const submit = async () => {
    for (const f of entity.fields || []) {
      if (f.required && (form[f.name] === '' || form[f.name] == null || (Array.isArray(form[f.name]) && form[f.name].length === 0))) {
        toast.error(`${f.label} is required`); return;
      }
    }
    const payload = {};
    (entity.fields || []).forEach((f) => { if (form[f.name] !== '' && form[f.name] != null) payload[f.name] = form[f.name]; });
    payload.status = form.__status;
    setSaving(true);
    try {
      if (isEdit) { await api.catalogUpdate(entity.key, initial.id, payload); toast.success(`${entity.label} updated`); }
      else { await api.catalogCreate(entity.key, payload); toast.success(`${entity.label} created (${form.__status})`); }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="catalog-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${entity.label}` : `Add ${entity.label}`}</DialogTitle>
          {entity.compliance_gated && <DialogDescription className="flex items-start gap-2 text-amber-600"><ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />Compliance-gated: kept PENDING_APPROVAL. No live quotes/purchase until an approved licensed partner is configured.</DialogDescription>}
        </DialogHeader>
        <div className="space-y-5">
          {Object.entries(groups).map(([group, fields]) => (
            <div key={group} className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {fields.map((f) => (
                  <div key={f.name} className={f.type === 'textarea' || f.type === 'multiimage' || f.type === 'image' ? 'sm:col-span-2' : ''}>
                    <Label className="text-xs">{f.label}{f.required && ' *'}</Label>
                    <FieldInput field={f} value={form[f.name]} onChange={setField} />
                    {f.help && <p className="text-[11px] text-muted-foreground mt-1">{f.help}</p>}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={form.__status} onValueChange={(v) => setField('__status', v)}>
              <SelectTrigger data-testid="field-status"><SelectValue /></SelectTrigger>
              <SelectContent>{(entity.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={onClose} data-testid="catalog-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="catalog-form-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? 'Save changes' : `Create ${entity.label}`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ------------------------------- Offers panel (Sheet) -------------------------------
const BLANK_OFFER = { provider_id: '', merchant: '', external_product_id: '', affiliate_program_id: '', affiliate_url: '', price: '', currency: 'INR', availability: 'in_stock' };

const OffersPanel = ({ entity, item, onClose }) => {
  const [offers, setOffers] = useState(null);
  const [embedded, setEmbedded] = useState(false);
  const [nf, setNf] = useState(BLANK_OFFER);
  const [test, setTest] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.catalogOffers(entity.key, item.id).then(({ data }) => { setOffers(data.offers || []); setEmbedded(!!data.embedded); }).catch(() => setOffers([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [item.id]);

  const runTest = async () => {
    if (!nf.affiliate_url) return;
    try { const { data } = await api.catalogTestLink(nf.affiliate_url); setTest(data); } catch { setTest({ valid: false, reason: 'error' }); }
  };
  const addOffer = async () => {
    if (!nf.provider_id || !nf.affiliate_url) { toast.error('Provider and affiliate URL are required'); return; }
    setSaving(true);
    try {
      await api.catalogCreateOffer(entity.key, item.id, { ...nf, price: nf.price === '' ? null : Number(nf.price) });
      toast.success('Offer added'); setNf(BLANK_OFFER); setTest(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add offer'); }
    finally { setSaving(false); }
  };
  const setOfferStatus = async (oid, status) => { try { await api.catalogSetOfferStatus(oid, status); toast.success(status); load(); } catch { toast.error('Failed'); } };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="offers-panel">
        <SheetHeader><SheetTitle className="flex items-center gap-2"><Link2 className="h-5 w-5 text-accent" />Offers · {item.title}</SheetTitle></SheetHeader>
        <p className="text-xs text-muted-foreground mt-1">Every offer routes through the central attribution engine <code className="text-[10px]">/api/go/{entity.vertical}/{'{offer_id}'}</code>. Affiliate URLs are validated (allowlist + SSRF) but never followed.</p>

        <div className="mt-4 space-y-2">
          {offers === null ? <Skeleton className="h-16 w-full rounded-xl" /> : offers.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="offers-empty">No offers yet.</div>
          ) : offers.map((o) => (
            <Card key={o.id} className="p-3 rounded-xl" data-testid={`offer-${o.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-sm">{o.merchant || o.partner_name || o.provider_id}</span>
                <Badge className={`rounded-full text-[10px] ${STATUS_CLS[o.status || 'active'] || ''}`}>{o.status || 'active'}</Badge>
                {o.is_demo && <Badge variant="outline" className="text-[10px] text-amber-600">demo</Badge>}
                {typeof o.clicks === 'number' && <Badge variant="outline" className="text-[10px]">{o.clicks} clicks</Badge>}
                {o.price != null && <span className="ml-auto text-sm font-semibold tabular-nums">{o.currency || '₹'} {Number(o.price).toLocaleString('en-IN')}</span>}
              </div>
              <p className="text-[11px] text-muted-foreground truncate mt-1">{o.affiliate_url}</p>
              {!embedded && (
                <div className="flex gap-1 mt-2 flex-wrap">
                  {(o.status || 'active') === 'active'
                    ? <Button size="sm" variant="outline" className="rounded-lg h-7 text-xs" onClick={() => setOfferStatus(o.id, 'disable')} data-testid={`offer-disable-${o.id}`}>Disable</Button>
                    : <Button size="sm" variant="outline" className="rounded-lg h-7 text-xs" onClick={() => setOfferStatus(o.id, 'restore')} data-testid={`offer-restore-${o.id}`}>Restore</Button>}
                  {(o.status || 'active') !== 'archived' && <Button size="sm" variant="ghost" className="rounded-lg h-7 text-xs" onClick={() => setOfferStatus(o.id, 'archive')} data-testid={`offer-archive-${o.id}`}>Archive</Button>}
                  <a href={api.goVerticalUrl(entity.vertical, o.id, {})} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" className="rounded-lg h-7 text-xs" data-testid={`offer-view-${o.id}`}><ExternalLink className="h-3.5 w-3.5 mr-1" />Destination</Button></a>
                </div>
              )}
            </Card>
          ))}
        </div>

        {embedded ? (
          <p className="mt-6 text-xs text-muted-foreground">This vertical stores a single affiliate link on the item itself — edit the item to change it.</p>
        ) : (
          <div className="mt-6 border-t border-border pt-4 space-y-3">
            <p className="text-sm font-semibold flex items-center gap-2"><Plus className="h-4 w-4" />Add offer</p>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Provider / merchant *</Label><Input value={nf.provider_id} onChange={(e) => setNf({ ...nf, provider_id: e.target.value })} placeholder="amazon" data-testid="offer-provider" /></div>
              <div><Label className="text-xs">Display name</Label><Input value={nf.merchant} onChange={(e) => setNf({ ...nf, merchant: e.target.value })} placeholder="Amazon.in" data-testid="offer-merchant" /></div>
              <div><Label className="text-xs">External product ID</Label><Input value={nf.external_product_id} onChange={(e) => setNf({ ...nf, external_product_id: e.target.value })} placeholder="ASIN / SKU" data-testid="offer-extid" /></div>
              <div><Label className="text-xs">Program ID</Label><Input value={nf.affiliate_program_id} onChange={(e) => setNf({ ...nf, affiliate_program_id: e.target.value })} data-testid="offer-program" /></div>
              <div><Label className="text-xs">Price</Label><Input type="number" value={nf.price} onChange={(e) => setNf({ ...nf, price: e.target.value })} data-testid="offer-price" /></div>
              <div><Label className="text-xs">Currency</Label><Select value={nf.currency} onValueChange={(v) => setNf({ ...nf, currency: v })}><SelectTrigger data-testid="offer-currency"><SelectValue /></SelectTrigger><SelectContent>{['INR', 'USD', 'EUR', 'GBP', 'AED'].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div>
              <Label className="text-xs">Affiliate / deep link URL *</Label>
              <div className="flex gap-2">
                <Input value={nf.affiliate_url} onChange={(e) => { setNf({ ...nf, affiliate_url: e.target.value }); setTest(null); }} placeholder="https://www.amazon.in/dp/…?tag=…" data-testid="offer-url" />
                <Button variant="outline" className="rounded-xl shrink-0" onClick={runTest} data-testid="offer-test-link">Test link</Button>
              </div>
              {test && (
                <div className={`mt-2 flex items-center gap-2 text-xs ${test.valid ? 'text-emerald-600' : 'text-red-600'}`} data-testid="offer-test-result">
                  {test.valid ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  {test.valid ? `Valid · ${test.host}` : `Rejected · ${test.reason}`}
                </div>
              )}
            </div>
            <Button onClick={addOffer} disabled={saving} className="w-full rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="offer-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add offer'}</Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ------------------------------- Audit dialog -------------------------------
const AuditDialog = ({ item, onClose }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.catalogAudit(item.id).then(({ data }) => setRows(data.audit || [])).catch(() => setRows([])); }, [item.id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="audit-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-accent" />Audit log · {item.title}</DialogTitle></DialogHeader>
        {rows === null ? <Skeleton className="h-24 w-full" /> : rows.length === 0 ? <p className="text-sm text-muted-foreground">No audit entries.</p> : (
          <div className="space-y-2">{rows.map((r) => (
            <div key={r.audit_id} className="flex items-center gap-2 text-xs border-b border-border pb-2 last:border-0">
              <Badge variant="outline" className="text-[10px]">{r.action}</Badge>
              <span className="text-muted-foreground">{r.actor_email || 'system'}</span>
              <span className="ml-auto text-muted-foreground">{fmtDateTime(r.created_at)}</span>
            </div>
          ))}</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ------------------------------- Main page -------------------------------
export default function AdminCatalog() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [params, setParams] = useSearchParams();
  const [denied, setDenied] = useState(false);
  const [entities, setEntities] = useState(null);
  const [activeKey, setActiveKey] = useState(params.get('entity') || 'store_product');
  const [list, setList] = useState(null);
  const [q, setQ] = useState('');
  const [statusF, setStatusF] = useState('all');
  const [demoF, setDemoF] = useState('all');
  const [formItem, setFormItem] = useState(undefined); // undefined=closed, null=add, obj=edit
  const [offersItem, setOffersItem] = useState(null);
  const [auditItem, setAuditItem] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkAction, setBulkAction] = useState('publish');
  const [reseeding, setReseeding] = useState(false);

  const active = useMemo(() => (entities || []).find((e) => e.key === activeKey), [entities, activeKey]);

  useEffect(() => { document.title = 'Universal Catalog | Admin'; if (!loading && !user) navigate('/login'); }, [user, loading, navigate]);
  useEffect(() => { if (user) api.catalogEntities().then(({ data }) => setEntities(data.entities)).catch((e) => { if (e?.response?.status === 403) setDenied(true); }); }, [user]);

  const loadList = () => {
    if (!activeKey) return;
    setList(null); setSelected([]);
    const p = {};
    if (q) p.q = q;
    if (statusF !== 'all') p.status = statusF;
    if (demoF !== 'all') p.demo = demoF;
    api.catalogList(activeKey, p).then(({ data }) => setList(data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); else setList({ items: [] }); });
  };
  useEffect(() => { if (user && entities) loadList(); /* eslint-disable-next-line */ }, [user, entities, activeKey, statusF, demoF]);

  const switchEntity = (key) => { setActiveKey(key); setParams({ entity: key }); setQ(''); setStatusF('all'); setDemoF('all'); };

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); loadList(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const doDelete = async () => { if (!confirmDel) return; await act(() => api.catalogDelete(activeKey, confirmDel.id), 'Deleted'); setConfirmDel(null); };
  const runBulk = async () => { if (selected.length === 0) { toast.error('Select rows first'); return; } await act(() => api.catalogBulk(activeKey, selected, bulkAction), `Bulk ${bulkAction} applied`); };
  const reseed = async () => { if (!RESEED[activeKey]) return; setReseeding(true); try { await RESEED[activeKey](); toast.success('Demo catalog reseeded'); loadList(); } catch { toast.error('Reseed failed'); } finally { setReseeding(false); } };
  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  if (loading) return <Layout><div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div></Layout>;
  if (denied) return <Layout><div className="max-w-2xl mx-auto py-24 text-center"><AlertTriangle className="h-10 w-10 mx-auto mb-3 text-amber-500" /><h1 className="font-display text-2xl font-semibold">Staff access required</h1><p className="text-muted-foreground mt-1">Your account cannot access the admin catalog.</p></div></Layout>;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2"><Layers className="h-6 w-6 text-accent" />Universal Catalog</h1>
          <Button variant="outline" className="rounded-xl" onClick={() => navigate('/admin')} data-testid="catalog-back">← Admin dashboard</Button>
        </div>
        <p className="text-sm text-muted-foreground mb-5">One admin system for every vertical — full lifecycle CRUD, offers &amp; central attribution, RBAC + audit.</p>

        {/* Entity tabs */}
        {!entities ? <Skeleton className="h-10 w-full rounded-xl mb-5" /> : (
          <div className="flex flex-wrap gap-2 mb-5" data-testid="catalog-entity-tabs">
            {entities.map((e) => (
              <button key={e.key} onClick={() => switchEntity(e.key)} data-testid={`entity-tab-${e.key}`}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${activeKey === e.key ? 'bg-accent text-accent-foreground border-accent' : 'bg-background border-border hover:border-accent/60'}`}>
                {e.label_plural} <span className="opacity-70 text-xs">· {e.count}</span>
              </button>
            ))}
          </div>
        )}

        {active && (
          <>
            {active.compliance_gated && (
              <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 flex items-start gap-2" data-testid="compliance-banner">
                <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" /> Insurance is compliance-gated. Plans stay <b className="mx-1">PENDING_APPROVAL</b> and are never sold/quoted in-app until an approved, licensed partner is connected. Demo plans must never be shown as real insurance.
              </div>
            )}

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="catalog-toolbar">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadList()} placeholder="Search…" className="pl-8 w-48" data-testid="catalog-search" />
              </div>
              <Select value={statusF} onValueChange={setStatusF}><SelectTrigger className="w-36" data-testid="catalog-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All statuses</SelectItem>{(active.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              <Select value={demoF} onValueChange={setDemoF}><SelectTrigger className="w-32" data-testid="catalog-demo-filter"><SelectValue placeholder="Data" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Demo + Live</SelectItem><SelectItem value="live">Live only</SelectItem><SelectItem value="demo">Demo only</SelectItem></SelectContent></Select>
              <div className="ml-auto flex items-center gap-2">
                {RESEED[activeKey] && <Button variant="outline" className="rounded-xl" onClick={reseed} disabled={reseeding} data-testid="catalog-reseed"><RefreshCw className={`h-4 w-4 mr-1.5 ${reseeding ? 'animate-spin' : ''}`} />Reseed demo</Button>}
                {active.create_allowed !== false
                  ? <Button className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setFormItem(null)} data-testid="catalog-add"><Plus className="h-4 w-4 mr-1.5" />Add {active.label}</Button>
                  : <Badge variant="outline" className="text-[11px] text-muted-foreground" data-testid="catalog-no-add">Authored in {active.key === 'itinerary' ? 'Creator Builder' : 'source module'}</Badge>}
              </div>
            </div>

            {/* Bulk bar */}
            {selected.length > 0 && (
              <div className="flex items-center gap-2 mb-3 rounded-xl bg-secondary/60 border border-border px-3 py-2 text-sm" data-testid="catalog-bulk-bar">
                <span>{selected.length} selected</span>
                <Select value={bulkAction} onValueChange={setBulkAction}><SelectTrigger className="w-40 h-8" data-testid="bulk-action-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{['publish', 'hide', 'archive', 'restore', 'feature', 'unfeature', ...(active.delete_allowed ? ['delete'] : [])].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select>
                <Button size="sm" className="rounded-lg" onClick={runBulk} data-testid="bulk-apply">Apply</Button>
                <Button size="sm" variant="ghost" className="rounded-lg" onClick={() => setSelected([])}>Clear</Button>
              </div>
            )}

            {/* List */}
            {list === null ? (
              <div className="space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full rounded-2xl" />)}</div>
            ) : list.items.length === 0 ? (
              <Card className="p-12 rounded-2xl text-center" data-testid="catalog-empty"><Tag className="h-10 w-10 mx-auto text-muted-foreground mb-2" /><p className="font-semibold">No {active.label_plural.toLowerCase()} found</p><p className="text-sm text-muted-foreground">Try clearing filters or add a new {active.label.toLowerCase()}.</p></Card>
            ) : (
              <div className="grid gap-3" data-testid="catalog-list">
                {list.items.map((it) => (
                  <Card key={it.id} className="p-4 rounded-2xl flex items-center gap-3" data-testid={`catalog-row-${it.id}`}>
                    <Checkbox checked={selected.includes(it.id)} onCheckedChange={() => toggleSel(it.id)} data-testid={`row-select-${it.id}`} />
                    <div className="h-14 w-14 rounded-lg bg-secondary overflow-hidden shrink-0">{it.image && <img src={api.mediaUrl(it.image)} alt="" className="w-full h-full object-cover" />}</div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm truncate">{it.title}</p>
                        <Badge className={`rounded-full text-[10px] ${STATUS_CLS[it.status] || 'bg-muted'}`} data-testid={`row-status-${it.id}`}>{it.status}</Badge>
                        {it.is_demo ? <Badge variant="outline" className="text-[10px] text-amber-600">demo</Badge> : it.is_live ? <Badge variant="outline" className="text-[10px] text-emerald-600">live</Badge> : null}
                        {it.approval_state && <Badge variant="outline" className="text-[10px] text-amber-600">{it.approval_state}</Badge>}
                        {it.featured && <Badge className="text-[10px] bg-accent text-accent-foreground">featured</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{it.subtitle}{active.supports_offers ? ` · ${it.offer_count} offers` : ''} · {it.clicks} clicks</p>
                    </div>
                    <div className="flex gap-1 flex-wrap justify-end shrink-0">
                      <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => setFormItem(it.raw ? { ...it.raw, id: it.id } : it)} data-testid={`row-edit-${it.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      {active.supports_offers && <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => setOffersItem(it)} data-testid={`row-offers-${it.id}`}><Link2 className="h-3.5 w-3.5 mr-1" />Offers</Button>}
                      {it.status !== 'published' ? <Button size="sm" className="rounded-lg h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => act(() => api.catalogSetStatus(activeKey, it.id, 'publish'), 'Published')} data-testid={`row-publish-${it.id}`}>Publish</Button>
                        : <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => act(() => api.catalogSetStatus(activeKey, it.id, 'hide'), 'Hidden')} data-testid={`row-hide-${it.id}`}>Hide</Button>}
                      {active.feature && <Button size="sm" variant="ghost" className="rounded-lg h-8" onClick={() => act(() => api.catalogFeature(activeKey, it.id, !it.featured), it.featured ? 'Unfeatured' : 'Featured')} data-testid={`row-feature-${it.id}`}><Star className={`h-3.5 w-3.5 ${it.featured ? 'fill-accent text-accent' : ''}`} /></Button>}
                      {it.status !== 'archived' ? <Button size="sm" variant="ghost" className="rounded-lg h-8" onClick={() => act(() => api.catalogSetStatus(activeKey, it.id, 'archive'), 'Archived')} data-testid={`row-archive-${it.id}`}><Archive className="h-3.5 w-3.5" /></Button>
                        : <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => act(() => api.catalogSetStatus(activeKey, it.id, 'restore'), 'Restored')} data-testid={`row-restore-${it.id}`}><RotateCcw className="h-3.5 w-3.5" /></Button>}
                      {active.delete_allowed && <Button size="sm" variant="ghost" className="rounded-lg h-8 text-red-600 hover:text-red-700" onClick={() => setConfirmDel(it)} data-testid={`row-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                      <Button size="sm" variant="ghost" className="rounded-lg h-8" onClick={() => setAuditItem(it)} data-testid={`row-audit-${it.id}`}><History className="h-3.5 w-3.5" /></Button>
                      {publicUrl(activeKey, it) && <a href={publicUrl(activeKey, it)} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" className="rounded-lg h-8" data-testid={`row-view-${it.id}`}><Eye className="h-3.5 w-3.5" /></Button></a>}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {formItem !== undefined && active && <EntityForm entity={active} initial={formItem} onClose={() => setFormItem(undefined)} onSaved={() => { setFormItem(undefined); loadList(); }} />}
      {offersItem && active && <OffersPanel entity={active} item={offersItem} onClose={() => { setOffersItem(null); loadList(); }} />}
      {auditItem && <AuditDialog item={auditItem} onClose={() => setAuditItem(null)} />}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid="delete-confirm">
          <AlertDialogHeader><AlertDialogTitle>Delete “{confirmDel?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the item. Linked offers are archived (not deleted) to preserve historical attribution. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={doDelete} data-testid="delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
